import React, { Suspense, useEffect } from 'react';
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
} from 'react-router-dom';
import { useAppDispatch } from '../../redux/store';
import { getViewStateFromUrl } from '../../utils/getViewStateFromUrl';
import { PageViewStack, setupViewStack } from '../../redux/reducers/pageViewsReducer';
import { setViewStateLoaded } from '../../redux/reducers/viewStateLoadedReducer';
import useViewController from '../../hooks/useViewController';
import ViewControllerContext from '../../contexts/ViewControllerContext';
import LoadingFallback from '../LoadingFallback/LoadingFallbackComponent';

import {
  AboutPageComponent,
  Callback,
  HomeComponent,
  MainContentWrapperComponent,
  PrivacySectionComponent,
  ProfileLayout,
  ProfileViewComponent,
  SecuritySectionComponent,
  SkeletonComponent,
  ThemeEditor,
  ThemeSectionComponent,
  ThemeSettingsComponent,
} from '../../routes/routes';

const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<LoadingFallback />}>{children}</Suspense>
);

const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path={'/'} element={<Outlet />}>
      { /**
        * Основная часть приложения
        */
      }
      <Route
        path={'app'}
        element={
          <SuspenseWrapper>
            <SkeletonComponent />
          </SuspenseWrapper>
        }
      >
        <Route
          index
          element={
            <SuspenseWrapper>
              <HomeComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path={'page'}
          element={
            <SuspenseWrapper>
              <MainContentWrapperComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="old_theme"
          element={
            <SuspenseWrapper>
              <ThemeSettingsComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="theme"
          element={
            <SuspenseWrapper>
              <ThemeEditor />
            </SuspenseWrapper>
          }
        />
        <Route
          path="about"
          element={
            <SuspenseWrapper>
              <AboutPageComponent />
            </SuspenseWrapper>
          }
        />
        <Route path={'*'} element={<Navigate to={'/app'} replace={true} />} />
      </Route>

      { /**
        * Профиль просматриваемого пользователя
        */
      }
      <Route
        path="profile"
        element={
          <SuspenseWrapper>
            <ProfileLayout />
          </SuspenseWrapper>
        }
      >
        <Route
          index
          element={
            <SuspenseWrapper>
              <ProfileViewComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="security"
          element={
            <SuspenseWrapper>
              <SecuritySectionComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="theme"
          element={
            <SuspenseWrapper>
              <ThemeSectionComponent />
            </SuspenseWrapper>
          }
        />
        <Route
          path="privacy"
          element={
            <SuspenseWrapper>
              <PrivacySectionComponent />
            </SuspenseWrapper>
          }
        />
        <Route path="*" element={<Navigate to="/profile" replace />} />
      </Route>

      { /**
       * Авторизация
       */
      }
      <Route
        path="/callback"
        element={
          <SuspenseWrapper>
            <Callback />
          </SuspenseWrapper>
        }
      />

      { /**
        * Редиректы
        */
      }
      <Route path={'/'} element={<Navigate to={'/app'} replace={true} />} />
      <Route path={'*'} element={<Navigate to={'/app'} replace={true} />} />
    </Route>,
  ),
);

const RouterComponent = () => {
  const dispatch = useAppDispatch();
  const viewController = useViewController();

  /**
   * Загружает данные из localStorage или по uuid.
   * Оповещает компоненты, что хлебные крошки загрузились.
   */
  useEffect(() => {
    const load = async () => {
      try {
        const encodedState = await getViewStateFromUrl();
        let viewStack: PageViewStack;

        if (encodedState) viewStack = JSON.parse(encodedState);
        else {
          const storedViewStack = sessionStorage.getItem('viewStack');
          viewStack = storedViewStack ? JSON.parse(storedViewStack) : [];
        }
        dispatch(setupViewStack(viewStack));
      } catch (err) {
        console.error('Ошибка при восстановлении состояния страницы:', err);
      } finally {
        dispatch(setViewStateLoaded(true));
      }
    };

    load();
  }, [dispatch]);

  return (
    <ViewControllerContext.Provider value={viewController}>
      <RouterProvider router={router} />
    </ViewControllerContext.Provider>
  );
};

export default RouterComponent;
